import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environment/environment';
import { ApiService } from '../http/api.service';

export interface LoginPayload {
  emailOrPhone: string;
  password: string;
  firebaseToken?: string;
}

export interface AuthUserData {
  id?: number;
  name?: string;
  email?: string;
  phoneNumber?: string;
  profileImage?: string;
  role?: string;
  roles?: string;
  levelId?: number;
  guardianId?: number;
}

export interface AuthDataResponse {
  user: AuthUserData;
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken?: string;
  refreshTokenExpiresAt?: string;
}

export interface LoginResponse {
  success: boolean;
  message: string | null;
  data: AuthDataResponse | null;
  errors: string[] | null;
}

interface UserProfile {
  nameid: string;
  email: string;
  image: string;
  EnName: string;
  ArName: string;
  gender: string;
  roles: string;
  company: string;
  is_hr: any;
  hr_company: string;
  hr_subsidiary: any;
}

export interface RegisterStudentPayload {
  name: string;
  password: string;
  email?: string | null;
  phoneNumber?: string | null;
  parentWhatsApp?: string | null;
  instructorCode?: string | null;
  levelId?: number | null;
  guardianId?: number | null;
  profileImage?: File | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private api: ApiService,
    private router: Router
  ) {}
  private readonly TOKEN_KEY = 'auth_token';
  private readonly is_hr = 'is_hr';
  private readonly EXPIRATION_KEY = 'token_expiration';
  private readonly CURRENT_EMPLOYEE = 'current_employee';
  private readonly CURRENT_COMPANY = 'current_company';
  private currentUser: UserProfile | null = null;

  private readonly userProfileSubject = new BehaviorSubject<any>(this.getUserDataFromStorage());
  public readonly userProfile$ = this.userProfileSubject.asObservable();

  getUserDataFromStorage(): any {
    const userDataStr = localStorage.getItem('user_data');
    if (userDataStr) {
      try {
        return JSON.parse(userDataStr);
      } catch {}
    }
    return null;
  }

  updateUserData(updatedData: any): void {
    const current = this.getUserDataFromStorage() || {};
    const merged = { ...current, ...updatedData };
    localStorage.setItem('user_data', JSON.stringify(merged));
    this.userProfileSubject.next(merged);
  }

  notifyProfileUpdated(): void {
    this.userProfileSubject.next(this.getUserDataFromStorage());
  }

  login(payload: LoginPayload): Observable<any> {
    const body = {
      emailOrPhone: payload.emailOrPhone,
      password: payload.password,
      firebaseToken: payload.firebaseToken || 'web_token',
    };
    return this.http.post<any>(
      `${environment.eduBaseURL}/auth/login`,
      body
    );
  }

  registerStudent(payload: RegisterStudentPayload): Observable<any> {
    const formData = new FormData();
    formData.append('Name', payload.name);
    formData.append('Password', payload.password);

    if (payload.email) {
      formData.append('Email', payload.email);
    }
    if (payload.phoneNumber) {
      formData.append('PhoneNumber', payload.phoneNumber);
    }
    if (payload.parentWhatsApp) {
      formData.append('ParentWhatsApp', payload.parentWhatsApp);
    }
    if (payload.instructorCode) {
      formData.append('InstructorCode', payload.instructorCode);
    }
    if (payload.levelId != null && !isNaN(Number(payload.levelId))) {
      formData.append('LevelId', String(payload.levelId));
    } else {
      formData.append('LevelId', '0');
    }
    if (payload.guardianId != null && !isNaN(Number(payload.guardianId))) {
      formData.append('GuardianId', String(payload.guardianId));
    } else {
      formData.append('GuardianId', '0');
    }
    if (payload.profileImage instanceof File) {
      formData.append('ProfileImage', payload.profileImage);
    }

    return this.http.post<any>(
      `${environment.eduBaseURL}/auth/register/student`,
      formData,
      { headers: { accept: 'text/plain' } }
    );
  }

  private readonly REFRESH_TOKEN_KEY = 'refresh_token';

  Login(formData: FormData) {
    return this.api.post('Accounts/login', formData);
  }

  setAuthData(token: string, expiresAt?: string, user?: AuthUserData, refreshToken?: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem('token', token);
    localStorage.setItem('accessToken', token);

    if (refreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
      localStorage.setItem('refreshToken', refreshToken);
    }
    if (expiresAt) {
      localStorage.setItem(this.EXPIRATION_KEY, expiresAt);
    }
    if (user) {
      localStorage.setItem('user_data', JSON.stringify(user));
      localStorage.setItem('user_role', user.role || user.roles || '');
    }
    this.decodeUserData(token);
  }

  getRefreshToken(): string | null {
    return (
      localStorage.getItem(this.REFRESH_TOKEN_KEY) ||
      localStorage.getItem('refreshToken') ||
      localStorage.getItem('refresh_token')
    );
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    const accessToken = this.getToken();

    if (!refreshToken) {
      return new Observable((obs) => {
        obs.error(new Error('No refresh token available'));
      });
    }

    const payload = {
      accessToken: accessToken || '',
      refreshToken: refreshToken,
      token: accessToken || '',
    };

    return this.http.post<any>(`${environment.eduBaseURL}/auth/refresh-token`, payload);
  }

  clearAuthStorage(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem('token');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('jwt');
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem(this.EXPIRATION_KEY);
    localStorage.removeItem('user_data');
    localStorage.removeItem('user_role');
    localStorage.removeItem(this.CURRENT_EMPLOYEE);
    localStorage.removeItem(this.CURRENT_COMPANY);
    localStorage.removeItem(this.is_hr);
    this.currentUser = null;
  }

  private decodeUserData(token: string): void {
    try {
      if (!token || typeof token !== 'string') return;
      const cleanToken = token.startsWith('Bearer ') ? token.slice(7).trim() : token.trim();
      const decoded = jwtDecode<UserProfile>(cleanToken);
      this.currentUser = {
        ...decoded,
        image: decoded.image ? environment.base + decoded.image : '',
        is_hr: decoded.is_hr === 'true' || decoded.is_hr === true,
        hr_subsidiary: decoded.hr_subsidiary
          ? (Array.isArray(decoded.hr_subsidiary) ? decoded.hr_subsidiary : String(decoded.hr_subsidiary).split(','))
          : [],
      };
      if (this.currentUser) {
        localStorage.setItem(this.is_hr, String(this.currentUser.is_hr));
        if (this.currentUser.company) {
          localStorage.setItem(this.CURRENT_COMPANY, this.currentUser.company);
        }
        if (!this.currentUser.is_hr && this.currentUser.nameid) {
          localStorage.setItem(this.CURRENT_EMPLOYEE, this.currentUser.nameid);
        }
      }
    } catch (error) {
      console.warn('Token decoding failed:', error);
      this.currentUser = null;
    }
  }

  // return user data
  getCurrentUser(): UserProfile | null {
    if (!this.currentUser && this.getToken()) {
      this.decodeUserData(this.getToken()!);
    }
    return this.currentUser;
  }
  getToken(): string | null {
    return (
      localStorage.getItem(this.TOKEN_KEY) ||
      localStorage.getItem('token') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('user_token') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('jwt')
    );
  }

  getStudentLevelId(): number | null {
    const userDataStr = localStorage.getItem('user_data');
    if (userDataStr) {
      try {
        const u = JSON.parse(userDataStr);
        const levelId = u?.levelId ?? u?.LevelId ?? u?.level_id;
        if (levelId != null && !isNaN(Number(levelId))) {
          return Number(levelId);
        }
      } catch {}
    }
    const token = this.getToken();
    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        const levelId = decoded?.levelId ?? decoded?.LevelId ?? decoded?.level_id ?? decoded?.Level;
        if (levelId != null && !isNaN(Number(levelId))) {
          return Number(levelId);
        }
      } catch {}
    }
    return null;
  }

  getTeacherSubjectId(): number | null {
    const userDataStr = localStorage.getItem('user_data');
    if (userDataStr) {
      try {
        const u = JSON.parse(userDataStr);
        const subjectId = u?.subjectId ?? u?.SubjectId ?? u?.subject_id;
        if (subjectId != null && !isNaN(Number(subjectId))) {
          return Number(subjectId);
        }
      } catch {}
    }
    const token = this.getToken();
    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        const subjectId = decoded?.subjectId ?? decoded?.SubjectId ?? decoded?.subject_id ?? decoded?.Subject;
        if (subjectId != null && !isNaN(Number(subjectId))) {
          return Number(subjectId);
        }
      } catch {}
    }
    return null;
  }

  getProfileImage(): string | null {
    const hostUrl = environment.eduBaseURL.replace(/\/api\/?$/, '');
    const userDataStr = localStorage.getItem('user_data');
    if (userDataStr) {
      try {
        const u = JSON.parse(userDataStr);
        const img = u?.profileImage ?? u?.ProfileImage ?? u?.image ?? u?.avatarUrl;
        if (img && typeof img === 'string' && img.trim()) {
          if (img.startsWith('http')) return encodeURI(img);
          return encodeURI(`${hostUrl}${img.startsWith('/') ? '' : '/'}${img}`);
        }
      } catch {}
    }
    const token = this.getToken();
    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        const img = decoded?.profileImage ?? decoded?.ProfileImage ?? decoded?.image ?? decoded?.avatarUrl;
        if (img && typeof img === 'string' && img.trim()) {
          if (img.startsWith('http')) return encodeURI(img);
          return encodeURI(`${hostUrl}${img.startsWith('/') ? '' : '/'}${img}`);
        }
      } catch {}
    }
    return null;
  }

  getTeacherName(): string {
    const userDataStr = localStorage.getItem('user_data');
    if (userDataStr) {
      try {
        const u = JSON.parse(userDataStr);
        if (u?.name) return u.name;
        if (u?.Name) return u.Name;
      } catch {}
    }
    const token = this.getToken();
    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        if (decoded?.name) return decoded.name;
        if (decoded?.Name) return decoded.Name;
        if (decoded?.sub) return decoded.sub;
      } catch {}
    }
    return '';
  }

  getPlatformName(): string {
    // 1. Direct localStorage keys
    const directKeys = [
      'platformName',
      'platform_name',
      'PlatformName',
      'platform',
      'Platform',
      'company_name',
      'companyName',
      'CompanyName',
      'current_company',
      'company',
      'brandName',
      'brand_name',
    ];
    for (const k of directKeys) {
      const v = localStorage.getItem(k);
      if (v && typeof v === 'string' && v.trim() && !v.startsWith('{') && !v.startsWith('[')) {
        if (isNaN(Number(v))) {
          return v.trim();
        }
      }
    }

    // 2. Objects in localStorage (user_data, user, etc.)
    const objectKeys = ['user_data', 'user', 'currentUser', 'auth_data', 'userData'];
    for (const objKey of objectKeys) {
      const str = localStorage.getItem(objKey);
      if (str) {
        try {
          const u = JSON.parse(str);
          if (u && typeof u === 'object') {
            const found = this.extractPlatformNameFromObject(u);
            if (found) return found;
          }
        } catch {}
      }
    }

    // 3. Check JWT token
    const token = this.getToken();
    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        if (decoded && typeof decoded === 'object') {
          const found = this.extractPlatformNameFromObject(decoded);
          if (found) return found;
        }
      } catch {}
    }

    return '';
  }

  extractPlatformNameFromObject(obj: any): string {
    if (!obj || typeof obj !== 'object') return '';

    // Direct priority fields
    const candidates = [
      'platformName',
      'PlatformName',
      'platform',
      'Platform',
      'platform_name',
      'Platform_Name',
      'platformTitle',
      'PlatformTitle',
      'companyName',
      'CompanyName',
      'company',
      'Company',
      'hr_company',
      'brandName',
      'BrandName',
      'centerName',
      'CenterName',
      'center',
      'Center',
      'academyName',
      'AcademyName',
      'academy',
      'Academy',
      'schoolName',
      'SchoolName',
      'school',
      'School',
      'subdomain',
      'Subdomain',
      'domain',
      'Domain',
      'tenantName',
      'TenantName',
      'tenant',
      'Tenant',
    ];

    for (const k of candidates) {
      const val = obj[k];
      if (typeof val === 'string' && val.trim() && isNaN(Number(val))) {
        return val.trim();
      }
    }

    // Iterate through all claims for custom claim URIs or any key
    for (const k of Object.keys(obj)) {
      const lower = k.toLowerCase();
      if (
        (lower.includes('platform') ||
          lower.includes('brand') ||
          lower.includes('company') ||
          lower.includes('center') ||
          lower.includes('academy') ||
          lower.includes('school') ||
          lower.includes('tenant')) &&
        !lower.includes('id') &&
        !lower.includes('url') &&
        !lower.includes('image') &&
        !lower.includes('icon') &&
        !lower.includes('date') &&
        !lower.includes('time') &&
        !lower.includes('path')
      ) {
        const val = obj[k];
        if (typeof val === 'string' && val.trim() && isNaN(Number(val))) {
          return val.trim();
        }
      }
    }

    return '';
  }
  getCurrentCompany(): string | null {
    return localStorage.getItem(this.CURRENT_COMPANY);
  }
  getCurrentEmployee(): string | null {
    return localStorage.getItem(this.CURRENT_EMPLOYEE);
  }

  isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) return false;

    const expiration = localStorage.getItem(this.EXPIRATION_KEY);
    if (expiration) {
      const expDate = new Date(expiration);
      if (!isNaN(expDate.getTime())) {
        return new Date() < expDate;
      }
    }

    try {
      const cleanToken = token.startsWith('Bearer ') ? token.slice(7).trim() : token.trim();
      const decoded: any = jwtDecode(cleanToken);
      if (decoded && decoded.exp) {
        return Date.now() < decoded.exp * 1000;
      }
    } catch {
      return false;
    }
    return true;
  }

  isLoggedIn(): boolean {
    return !!this.getToken() && this.isTokenValid();
  }

  // return isHr
  isHR(): boolean {
    const user = this.getCurrentUser();
    return user?.is_hr || false;
  }
  isEmployee(): boolean {
    const user = this.getCurrentUser();
    return !user?.is_hr;
  }
  // return Subsidiaries Companies
  getHrSubsidiaries(): string[] {
    return this.getCurrentUser()?.hr_subsidiary || [];
  }

  logout(redirectUrl: string | null = '/auth/login'): void {
    this.clearAuthStorage();
    if (redirectUrl) {
      this.router.navigate([redirectUrl]);
    }
  }
}

// export class AuthService {
//   private authStatus = new BehaviorSubject<boolean>(this.isTokenValid());
//   authStatus$ = this.authStatus.asObservable();

//   // ... existing code ...

//   setAuthData(token: string, expiresAt: string): void {
//     localStorage.setItem(this.TOKEN_KEY, token);
//     localStorage.setItem(this.EXPIRATION_KEY, expiresAt);
//     this.decodeUserData(token);
//     this.authStatus.next(true);
//   }

//   logout(): void {
//     // ... existing cleanup code ...
//     this.authStatus.next(false);
//   }

//   // Add this to automatically update auth status when token is checked
//   private checkAndUpdateAuthStatus(): boolean {
//     const isAuthenticated = this.isTokenValid();
//     if (isAuthenticated !== this.authStatus.value) {
//       this.authStatus.next(isAuthenticated);
//     }
//     return isAuthenticated;
//   }
// }
